import type { ImportFieldSpec, ParsedUnit } from '../SpreadsheetImportModal';

// RECONCILED with the real codebase (real file wins): the plan said to import
// `isValidThaiTaxId` from `@dcc-erp/shared`, but that helper is NOT exported there —
// it lives only in the API (`apps/api/src/modules/doc-ingest/tax-id.ts`), which the
// web bundle cannot import. We therefore inline the SAME mod-11 algorithm here so
// the importer's check digit matches the backend's exactly (no divergence risk:
// both reduce to `(11 - (sum % 11)) % 10 === lastDigit`).
const THAI_DIGITS = '๐๑๒๓๔๕๖๗๘๙';
function thaiDigitsToArabic(s: string): string {
  return s.replace(/[๐-๙]/g, (d) => String(THAI_DIGITS.indexOf(d)));
}
function isValidThaiTaxId(idRaw: string): boolean {
  const id = thaiDigitsToArabic(idRaw ?? '').replace(/\s/g, '');
  if (!/^\d{13}$/.test(id)) return false;
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += Number(id[i]) * (13 - i);
  return (11 - (sum % 11)) % 10 === Number(id[12]);
}

export interface SupplierImportRow {
  code: string;
  nameTh: string;
  taxId: string | null;
  branchCode: string;
  terms: string | null;
}

export const SUPPLIER_IMPORT_FIELDS: ImportFieldSpec[] = [
  { col: 'code', label: 'รหัสผู้ขาย', required: true, hint: 'เช่น S-00001' },
  { col: 'nameTh', label: 'ชื่อผู้ขาย', required: true, hint: 'ชื่อนิติบุคคลตามกรมสรรพากร' },
  { col: 'taxId', label: 'เลขประจำตัวผู้เสียภาษี', required: true, hint: '13 หลัก (ตรวจ mod-11)' },
  { col: 'branchCode', label: 'รหัสสาขา', required: false, hint: 'เช่น 00000 (สำนักงานใหญ่)' },
  { col: 'terms', label: 'เงื่อนไขชำระ', required: false, hint: 'เช่น N30' },
];

const REQUIRED = ['code', 'nameTh', 'taxId'];

export function parseSupplierImportGrid(
  grid: string[][],
  existingTaxIds: Set<string>,
): { units: ParsedUnit<SupplierImportRow>[]; headerError: string | null } {
  const header = (grid[0] ?? []).map((h) => h.trim());
  const missing = REQUIRED.filter((r) => !header.includes(r));
  if (missing.length)
    return { units: [], headerError: `ขาดคอลัมน์ที่จำเป็น: ${missing.join(', ')}` };
  const idx = (c: string) => header.indexOf(c);
  const seen = new Set<string>(); // within-file taxId dup guard
  const units: ParsedUnit<SupplierImportRow>[] = [];

  for (let r = 1; r < grid.length; r++) {
    const row = grid[r] ?? [];
    const rowNo = r + 1;
    const get = (c: string) => (idx(c) >= 0 ? (row[idx(c)] ?? '').trim() : '');
    const code = get('code');
    const nameTh = get('nameTh');
    const taxId = get('taxId');
    const branchCode = get('branchCode') || '00000';
    const terms = get('terms') || null;
    if (!code || !nameTh || !taxId) {
      units.push({ rowNo, data: null, error: 'ต้องระบุ code, nameTh และ taxId' });
      continue;
    }
    if (!isValidThaiTaxId(taxId)) {
      units.push({
        rowNo,
        data: null,
        error: 'เลขประจำตัวผู้เสียภาษีไม่ถูกต้อง (mod-11)',
      });
      continue;
    }
    if (seen.has(taxId)) {
      units.push({ rowNo, data: null, error: `เลขผู้เสียภาษีซ้ำในไฟล์: ${taxId}` });
      continue;
    }
    // A taxId already in the master is NOT importable: the backend
    // (`SuppliersService.createWithBranch`) rejects an existing entity-level taxId
    // with `ConflictException('ผู้ขายรายนี้มีอยู่แล้ว')`, and this importer only
    // ever POSTs new suppliers (it has no per-branch edit/upsert path). So mark
    // the row as an error here rather than promise a phantom "update" the import
    // run can never deliver — keeping the preview/result tallies honest. Editing
    // an existing supplier is done in the per-supplier edit flow, not bulk import.
    if (existingTaxIds.has(taxId)) {
      units.push({
        rowNo,
        data: null,
        error: `มีผู้ขายเลขภาษีนี้อยู่แล้วในระบบ: ${taxId} (แก้ไขผ่านหน้าผู้ขายแทนการนำเข้า)`,
      });
      continue;
    }
    seen.add(taxId);
    units.push({
      rowNo,
      data: { code, nameTh, taxId, branchCode, terms },
      error: null,
      action: 'create',
    });
  }
  return { units, headerError: null };
}
